import React from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  useTheme,
  useMediaQuery,
  Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconReusable from '../../../components/IconReusable';

const ReusableModal = ({
  open,
  onClose,
  title = '',
  subtitle = '',
  children,
  maxWidth = '800px',
  topDesktop = '50%',
  topMobile = '20%',
  scrollable = true,
  maxHeight = '40px',
  showHeader = true,
  centerAlign = false,
  pt = 2,
  px = { md: 2, xs: 2 },
  pb = 3,
  hideBackdrop = false,
  modalSx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const paddingBottom = pb === 3 ? (isMobile ? 13 : 3) : pb;

  const getMaxHeight = () => {
    if (maxHeight) {
      if (isMobile) {
        return `calc(100vh - 40px)`;
      }
      return maxHeight;
    }
    return isMobile ? 'calc(100% - 40px)' : scrollable ? '80vh' : 'auto';
  };

  const getPositioning = () => {
    if (centerAlign && !isMobile) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }
    return {
      top: isMobile ? topMobile : topDesktop,
      left: isMobile ? 0 : '50%',
      transform: isMobile ? 'none' : 'translate(-32%, -50%)',
    };
  };

  const getWidth = () => {
    if (centerAlign && !isMobile) {
      return maxWidth;
    }
    return isMobile ? '100%' : maxWidth;
  };

  const getHeight = () => {
    if (centerAlign && !isMobile) {
      return maxHeight ? 'fit-content' : 'auto';
    }
    return isMobile ? '100%' : 'auto';
  };

  const modalContent = (
    <Box
      sx={{
        position: 'absolute',
        top: isMobile ? topMobile : topDesktop,
        left: isMobile ? 0 : '50%',
        transform: isMobile ? 'none' : 'translate(-32%, -50%)',
        width: isMobile ? '100%' : maxWidth,
        height: isMobile ? `calc(100vh - ${maxHeight})` : 'auto',
        maxHeight: isMobile ? `calc(100vh - ${maxHeight})` : scrollable ? '80vh' : 'auto',
         ...getPositioning(),
        bgcolor: 'white',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomLeftRadius: centerAlign || !isMobile ? 16 : 0,
        borderBottomRightRadius: centerAlign || !isMobile ? 16 : 0,
        boxShadow: 24,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {showHeader && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            py: 2,
            px: { md: 2, xs: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderBottom: '1px solid #D9D9D9',
          }}
        >
            <Typography variant="h1">{title}</Typography>
            {onClose && (
              <Box sx={{ cursor: 'pointer' }}>
                <IconReusable name="Close" onClick={onClose} color='#333'/>
              </Box>
            )}
        </Box>
      )}

      {subtitle && (
        <Box sx={{ px: { md: 4, xs: 2 }, pb: 1 }}>
          <Typography variant='body1'>{subtitle}</Typography>
        </Box>
      )}

      <Box
        sx={{
          pt: pt,
          px: px,
          pb: paddingBottom,
          overflowY: scrollable ? 'auto' : 'unset',
          flex: 1,
          position: 'relative',
        }}
      >
        {children}
      </Box> 
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      hideBackdrop={hideBackdrop}
      sx={modalSx}
    >
      {isMobile ? (
        <Slide
          direction='up'
          in={open}
          mountOnEnter
          unmountOnExit
          timeout={{ enter: 500, exit: 300 }}
        >
          {modalContent}
        </Slide>
      ) : (
        modalContent
      )}
    </Modal>
  );
};

export default ReusableModal;
